import React, { useCallback, useEffect, memo, useState } from 'react';
import { ChevronLeft, ChevronRight, Megaphone, Newspaper, RefreshCw, X } from 'lucide-react';

const KIND_META = {
  promo: { label: 'Акция', Icon: Megaphone, chipClass: 'border-amber-400/35 bg-amber-950/40 text-amber-100' },
  news: { label: 'Новость', Icon: Newspaper, chipClass: 'border-sky-400/35 bg-sky-950/40 text-sky-100' },
  update: { label: 'Обновление', Icon: RefreshCw, chipClass: 'border-emerald-400/35 bg-emerald-950/40 text-emerald-100' },
};

/**
 * Модальная карусель новостей и акций при входе (аналог промо при запуске мобильных игр).
 * @param {{ slides: Array<Record<string, unknown>>, version: number, onDismiss: () => void }} props
 */
function LoginPromoCarouselInner({ slides, version, onDismiss }) {
  const [index, setIndex] = useState(0);
  const list = Array.isArray(slides) ? slides : [];
  const len = list.length;

  useEffect(() => {
    setIndex(0);
  }, [slides, version]);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem('seka_carousel_ack_version', String(Number(version) || 0));
    } catch {
      /* ignore */
    }
    onDismiss?.();
  }, [version, onDismiss]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        dismiss();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dismiss]);

  if (!len) return null;

  const safeIdx = Math.min(Math.max(0, index), len - 1);
  const slide = list[safeIdx];
  const kind = String(slide?.kind || 'promo').toLowerCase();
  const meta = KIND_META[kind] || KIND_META.promo;
  const { Icon, label, chipClass } = meta;
  const title = String(slide?.title || '').trim() || 'Без названия';
  const subtitle = String(slide?.subtitle || '').trim();
  const body = String(slide?.body || '').trim();
  const imageUrl = String(slide?.imageUrl || '').trim();
  const linkUrl = String(slide?.linkUrl || '').trim();
  const linkLabel = String(slide?.linkLabel || '').trim() || 'Подробнее';

  const goPrev = () => setIndex((i) => (i - 1 + len) % len);
  const goNext = () => setIndex((i) => (i + 1) % len);

  return (
    <div
      className="fixed inset-0 z-[118] flex items-center justify-center bg-black/75 px-3 py-[max(0.75rem,env(safe-area-inset-top,0px))] pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-promo-carousel-title"
    >
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Закрыть окно"
        onClick={dismiss}
      />
      <div className="relative z-[1] w-full max-w-md overflow-hidden rounded-2xl border border-cyan-400/25 bg-gradient-to-b from-[#0f2438] via-[#0b1828] to-[#060d14] shadow-[0_24px_80px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.08)]">
        <div className="flex items-start justify-between gap-2 border-b border-white/10 px-4 pb-3 pt-3.5 sm:px-5">
          <div className="min-w-0">
            <span
              className={`inline-flex items-center gap-1 rounded-lg border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${chipClass}`}
            >
              <Icon className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
              {label}
            </span>
            <h2 id="login-promo-carousel-title" className="mt-2 text-lg font-black leading-tight text-white sm:text-xl">
              {title}
            </h2>
            {subtitle ? <p className="mt-1 text-sm font-medium text-slate-300">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="lobby-focus shrink-0 rounded-xl border border-white/15 bg-black/35 p-2 text-slate-200 transition hover:border-cyan-300/40 hover:text-white"
            aria-label="Закрыть"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        {imageUrl ? (
          <div className="border-b border-white/10 bg-black/30">
            <img src={imageUrl} alt="" className="mx-auto max-h-[40vh] w-full object-contain" loading="lazy" />
          </div>
        ) : null}

        <div className="max-h-[min(42vh,320px)] overflow-y-auto px-4 py-3 sm:px-5">
          {body ? <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-200">{body}</p> : null}
          {!body && !imageUrl ? (
            <p className="text-sm text-slate-400">Нажмите «Закрыть», чтобы перейти в лобби.</p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-white/10 bg-black/20 px-4 py-3 sm:px-5">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {len > 1 ? (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  className="lobby-focus inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-black/30 text-white transition hover:border-cyan-300/35"
                  aria-label="Предыдущий слайд"
                >
                  <ChevronLeft className="h-5 w-5" aria-hidden />
                </button>
                <div className="flex min-w-0 flex-1 justify-center gap-1.5">
                  {list.map((s, i) => (
                    <button
                      key={String(s?.id ?? `dot-${i}`)}
                      type="button"
                      onClick={() => setIndex(i)}
                      className={`h-2 rounded-full transition ${i === safeIdx ? 'w-6 bg-cyan-400' : 'w-2 bg-white/25 hover:bg-white/40'}`}
                      aria-label={`Слайд ${i + 1} из ${len}`}
                      aria-current={i === safeIdx ? 'true' : undefined}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={goNext}
                  className="lobby-focus inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-black/30 text-white transition hover:border-cyan-300/35"
                  aria-label="Следующий слайд"
                >
                  <ChevronRight className="h-5 w-5" aria-hidden />
                </button>
              </>
            ) : (
              <span className="text-xs text-slate-500">v{Number(version) || 1}</span>
            )}
          </div>
          <div className="flex w-full shrink-0 flex-wrap justify-end gap-2 sm:w-auto">
            {linkUrl ? (
              <a
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="lobby-focus inline-flex items-center justify-center rounded-xl border border-cyan-400/40 bg-cyan-900/35 px-4 py-2 text-sm font-semibold text-cyan-100 transition hover:brightness-110"
              >
                {linkLabel}
              </a>
            ) : null}
            <button
              type="button"
              onClick={dismiss}
              className="lobby-focus inline-flex items-center justify-center rounded-xl border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/15"
            >
              Закрыть
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export const LoginPromoCarousel = memo(LoginPromoCarouselInner);
